import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FaFlag, FaTimes } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { reportService } from '../services/reportService';

const ReportButtonStyled = styled.button`
  background: none;
  border: 1px solid #dc3545;
  color: #dc3545;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #dc3545;
    color: white;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    gap: 0.35rem;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  
  h3 {
    color: #8B5A96;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  p {
    color: #666;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  
  label {
    display: block;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: #333;
  }
  
  select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-family: inherit;
    
    &:focus {
      outline: none;
      border-color: #8B5A96;
    }
  }
  
  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: #6c757d;
  color: white;
  
  &:hover:not(:disabled) {
    background: #5a6268;
  }
`;

const SubmitButton = styled(Button)`
  background: #dc3545;
  color: white;
  
  &:hover:not(:disabled) {
    background: #c82333;
  }
`;

const reportReasons = [
  'Inappropriate content',
  'Spam or misleading',
  'Harassment or abuse',
  'Violates terms of service',
  'Fraudulent activity',
  'Other'
];

const ReportButton = ({ subjectType, subjectId, subjectTitle }) => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleReport = async () => {
    if (!reason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    setSubmitting(true);
    
    try {
      await reportService.createReport({
        subjectType,
        subjectId,
        reason,
        description: description.trim() || null
      });

      toast.success('Report submitted successfully. Thank you for helping keep our community safe.');
      setShowModal(false);
      setReason('');
      setDescription('');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setReason('');
    setDescription('');
  };

  if (!user) {
    return null; // Don't show report button if user is not logged in
  }

  return (
    <>
      <ReportButtonStyled onClick={() => setShowModal(true)}>
        <FaFlag />
        Report
      </ReportButtonStyled>

      {showModal && (
        <Modal onClick={handleCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>
              <FaFlag />
              Report Content
            </h3>
            <p>
              Help us maintain a safe community by reporting content that violates our guidelines. 
              Your report will be reviewed by our moderation team.
            </p>

            <FormGroup>
              <label>Reason for reporting *</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="">Select a reason...</option>
                {reportReasons.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </FormGroup>

            <FormGroup>
              <label>Additional details (optional)</label>
              <textarea
                placeholder="Provide any additional context that might help us understand the issue..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormGroup>

            <ButtonContainer>
              <CancelButton onClick={handleCancel}>
                Cancel
              </CancelButton>
              <SubmitButton 
                onClick={handleReport}
                disabled={!reason || submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Report'}
              </SubmitButton>
            </ButtonContainer>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ReportButton;